import React, { useState, MouseEvent, ReactElement, useEffect, useMemo, useRef } from "react";
import cn from "classnames";
import { triggerSnowplowImpressionEvent } from "@themuse/snowplow-js-client";

import { User } from "../../types";
import { ArrowDownShape, ArrowUpShape, ArrowRightShape, CloseIcon, Hamburger, UserIcon, Airplane } from "../Icons";
import { UserMenu } from "./UserMenu";
import ComposableHeaderDropdown, { ComposableHeaderDropdownProviderProps } from "../Dropdowns/ComposableHeaderDropdown";
import { MuseColorLogo } from "../Logos";
import JobSearchInput from "./JobSearchInput";
import { navItems, NavItem } from "./navItems";
import Newsletter from "../Modals/Newsletter";

import styles from "./Header.module.scss";
import headerDropdownClasses from "./HeaderSearchDropdown.module.scss";

interface HeaderProps {
    activePath?: string;
    mainContainerId?: string;
    user?: User;
    onSignIn?: () => void;
    onSignOut?: () => void;
    className?: string;
    enableSearch?: boolean;
    enableCompanySearch?: boolean;
    companySearchDropdownProps?: ComposableHeaderDropdownProviderProps;
    searchUrl?: string;
    onSearchSubmit?: (keyword: string) => void;
    staticMobileHeader?: boolean;
    showSubMenus?: boolean;
    handleModalFormSubmit?: (values: object) => Promise<boolean>;
}

/**
 * Default user object with initial values for various properties.
 */
const defaultUser = {
    logged_in: false,
    view_bookings: false,
    company: null,
    location: null,
    cropped_name: "",
    email: "",
    favorite_posts: [],
    name: "",
    id: null
};

/**
 * Handles the mouse up event by removing focus from the current target element and stopping event propagation.
 */
const mouseUpHandler = (event: MouseEvent<HTMLElement>) => {
    event.currentTarget.blur();
    event.stopPropagation();
};

/**
 * Handles the mouse up event by removing focus from the current target element and stopping event propagation.
 */
const mouseUpLinkHandler = (event: MouseEvent<HTMLElement>) => {
    event.currentTarget.blur();
};

/**
 * The User Trigger shows a user icon when the user is logged in with functions to show the user panel.
 * When the user is not logged in, shows a link to the login page.
 */
const UserTrigger = (props: {
    handleClickSignIn?: () => void,
    mouseUpHandler?: (event: MouseEvent<HTMLElement>) => void,
    toggleUserMenu?: () => void,
    user?: User,
}): ReactElement | null => {
    const {
        handleClickSignIn,
        mouseUpHandler,
        toggleUserMenu,
        user = defaultUser,
    } = props;
    return (
        <div className={styles.navItem}>
            {user?.logged_in
                ? (
                    <button
                    className={cn(styles.navButton, styles.userButton)}
                    type="button"
                    aria-label="Open User Menu"
                    aria-haspopup="true"
                    onClick={toggleUserMenu}
                    onMouseUp={mouseUpHandler}
                    >
                        <UserIcon />
                    </button>
                )
                : (
                    <a
                    tabIndex={0}
                    className={cn(styles.signIn, styles.menuItemLink)}
                    aria-label="Sign In"
                    href="/user/login"
                    onClick={handleClickSignIn}
                    onMouseUp={mouseUpHandler}
                    >
                        Sign In
                    </a>
                )
            }
        </div>
    );
};

/**
 * The Hamburger Trigger shows a hamburger icon (only in mobile) with functions to show the global panel.
 */
const HamburgerTrigger = (props: {
    mouseUpHandler?: (event: MouseEvent<HTMLElement>) => void,
    toggleHamburgerMenu?: () => void,
}): ReactElement | null => {
    const {
        mouseUpHandler,
        toggleHamburgerMenu,
    } = props;
    return (
        <div className={styles.navItem}>
            <button
                type="button"
                className={cn(styles.navButton, styles.hamburgerButton)}
                aria-label="Open Navigation Menu"
                aria-haspopup="true"
                onClick={toggleHamburgerMenu}
                onMouseUp={mouseUpHandler}
            >
                <Hamburger />
            </button>
        </div>
    );
};

/**
 * Renders a sub navigation menu with a summary and list of sub menu items.
 *
 * @returns {JSX.Element} React element representing the sub navigation menu.
 */
const SubNav = ({ outlined, text, subNavItems }: NavItem): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);

    return (
    <>
        <div
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            className={styles.trigger}
        />
        <details className={ cn(styles.subMenu, { [styles.outlined]: outlined })} open={isOpen}>
            <summary className={styles.menuItem} aria-label={`Open ${text} Sub Menu`}>
                <span className={styles.menuItemLink}>{ text }</span>
                <ArrowUpShape className={styles.arrowUp} />
                <ArrowDownShape className={styles.arrowDown} />
            </summary>
            <ul
                className={styles.detailsList}
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
            >
                { subNavItems?.map(({ href, text, dataHeaderLink, showOnMobile }) => (
                    <li key={ dataHeaderLink } className={cn(styles.subMenuItem, {[styles.hiddenMobile]: !showOnMobile})}>
                        <a
                            className={ cn(styles.menuItemLink, styles.subMenuItemLink) }
                            href={href}
                        >
                            { text }
                        </a>
                    </li>
                )) ?? null }
                <li key="view-all-jobs" className={styles.subMenuItem}>
                    <a
                        className={ cn(styles.menuItemLink, styles.subMenuItemLink) }
                        href="/search/"
                    >
                        View All Jobs <ArrowRightShape />
                    </a>
                </li>
            </ul>
        </details>
    </>
    );
};

/**
 * A header main menu item link.
 */
const MenuItemLink = (props: {
    activePath: string,
    href: string,
    text: string,
    dataHeaderLink: string,
    clickHandler: (e: MouseEvent) => void;
}) => {
    const {
        activePath,
        href,
        text,
        dataHeaderLink,
        clickHandler
    } = props;
    return (
        <a
            className={cn(styles.menuItemLink, { [styles.active]: activePath?.startsWith(href) })}
            href={href}
            onClick={text === "Newsletter" ? clickHandler : () => null}
            onMouseUp={mouseUpLinkHandler}
            data-link={dataHeaderLink}
        >
            {text}{text === "Newsletter" ? <Airplane {...{ height: 24, width: 24 }} /> : null}
        </a>
    );
};

/**
 * A skip link to main content.
 */
const SkipContent = (props: { mainContainerId: string; }) => (
    <a
        tabIndex={0}
        aria-label="Skip to main content"
        className={styles.skipNav}
        href={`#${props.mainContainerId}`.replace("##", "#")}
    >
        Skip to main content
    </a>
);

/**
 * A The Muse logo with  link.
 */
const MuseLogoLink = () => (
    <a
        className={styles.logoAnchor}
        href="/"
        aria-label="Home Page"
        onMouseUp={(e: MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.blur();
        }}
    >
        <MuseColorLogo className={styles.logo} />
    </a>
);

/**
 * An extra newsletter link outside the nav for a smaller screens.
 */
const NewsletterExtraLink = (props: { clickHandler: (e: MouseEvent) => void; }) => (
    <div className={styles.newsletterWrapper}>
        <div className={styles.menuItem}>
            <button
                className={styles.newsletterButton}
                onClick={props.clickHandler}
                onMouseUp={mouseUpHandler}
                aria-label="Open Newsletter Modal"
            >
                Newsletter <Airplane {...{ height: 24, width: 24 }} />
            </button>
        </div>
    </div>
);

/**
 * A button to open and close the nav menu on smaller screens.
 */
const MobileToggle = (props: {
    menuOpen: boolean,
    userMenuOpen: boolean,
    setMenuOpen: (value: React.SetStateAction<boolean>) => void,
    setUserMenuOpen: (value: React.SetStateAction<boolean>) => void
}) => {
    const {
        menuOpen,
        userMenuOpen,
        setMenuOpen,
        setUserMenuOpen
    } = props;
    return (
        <button
            className={cn(styles.closeButton, { [styles.open]: menuOpen || userMenuOpen }, { [styles.mainMenuOpen]: menuOpen })}
            tabIndex={0}
            type="button"
            aria-label="Close Navigation Menu"
            onClick={() => {
                menuOpen && setMenuOpen(false);
                userMenuOpen && setUserMenuOpen(false);
            }}
            onMouseUp={mouseUpHandler}
        >
            <CloseIcon />
        </button>
    );
};

/**
 * A Header component for the website's header section with optional search inputs.
 */
const Header = ({
    activePath = "",
    className,
    mainContainerId = "",
    user,
    searchUrl = "",
    onSignIn = () => {},
    onSignOut = () => {},
    enableSearch = false,
    enableCompanySearch = false,
    companySearchDropdownProps,
    onSearchSubmit,
    staticMobileHeader = false,
    showSubMenus = false,
    handleModalFormSubmit = () => {
        console.error("Design System: Newsletter handleModalFormSubmit not supplied to the header!");
        return Promise.resolve(false);
    }
}: HeaderProps): JSX.Element => {
    const [showFormModal, setShowFormModal] = useState<boolean>(false);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);

    /**
     * Function that triggers a Snowplow impression event for the provided HTMLDivElement node.
     */
    const newsletterRef = (node: HTMLDivElement) =>  node && triggerSnowplowImpressionEvent(node);

    /**
     * Handles the success reference for triggering the Snowplow impression event on a given HTMLDivElement node.
     */
    const successRef = (node: HTMLDivElement) => node && triggerSnowplowImpressionEvent(node);

    const dropdownClasses = {
        container: `${headerDropdownClasses.container}`,
        containerOpened: `${headerDropdownClasses.containerOpened}`,
        inputContainer: headerDropdownClasses.inputContainer,
        inputContainerOpened: headerDropdownClasses.inputContainerOpened,
        input: headerDropdownClasses.input,
        menuOpened: headerDropdownClasses.menuOpened,
        menuList: headerDropdownClasses.menuList,
        listItem: headerDropdownClasses.listItem,
        listItemText: headerDropdownClasses.listItemText,
        closeButton: headerDropdownClasses.closeButton,
        inputClearButton: headerDropdownClasses.inputClearButton,
        clearButton: headerDropdownClasses.clearButton,
        postLabel: headerDropdownClasses.postLabel,
    };

    const companySearchProps = {
        classes: dropdownClasses,
        dropdownProps: companySearchDropdownProps || {
            inputId: "search-input",
            dropdownList: []
        }
    };

    // Function for triggering the newsletter modal to close
    const handleModalFormClose = () => setShowFormModal(false);

    // Function for triggering the newsletter click and show the modal.
    const handleHeaderNewsletterClick = (e: MouseEvent) => {
        e?.preventDefault?.();
        setShowFormModal(true);
    };
    return (
        <header className={cn(styles.header, {[styles.staticMobile]: staticMobileHeader}, className)}>
            <SkipContent {...{ mainContainerId }} />
            <MuseLogoLink />
            { enableSearch ? (
                <JobSearchInput {...{ className: styles.jobSearch, searchUrl, onSearchSubmit }} />
            ) : null }
            {enableCompanySearch && companySearchDropdownProps ? (
                <ComposableHeaderDropdown {...companySearchProps} />
            ) : null}
            <NewsletterExtraLink {...{ clickHandler: handleHeaderNewsletterClick }} />
            <nav className={styles.navItems}>
                <HamburgerTrigger toggleHamburgerMenu={() => setMenuOpen(true)}/>
                <ul className={cn(styles.menu, styles.mainMenu, { [styles.open]: menuOpen })}>
                    { navItems.map(({text, href, outlined, dataHeaderLink, subNavItems}: NavItem, index) => {
                        const hasSubNavItems = showSubMenus && (subNavItems?.length ?? 0) > 0;
                        const className = cn({ [styles.menuItem]: !hasSubNavItems, [styles.outlined]: outlined, [styles.menuItemSubNav]: hasSubNavItems })
                        return (
                            <li className={className} key={dataHeaderLink} data-key={dataHeaderLink}>
                                {hasSubNavItems ? (
                                    <SubNav {...{ ...navItems[index] }} />
                                ) : (
                                    <MenuItemLink {...{
                                        activePath,
                                        href,
                                        text,
                                        dataHeaderLink,
                                        clickHandler: handleHeaderNewsletterClick
                                    }} />
                                )}
                            </li>
                        )
                    }) }
                </ul>
                <UserTrigger user={user} toggleUserMenu={() => setUserMenuOpen(true)} handleClickSignIn={onSignIn} />
                {user && <UserMenu user={user} menuOpen={userMenuOpen} onSignOut={onSignOut} />}
                <MobileToggle {...{ menuOpen, userMenuOpen, setMenuOpen, setUserMenuOpen }} />
            </nav>
            <Newsletter {...{ showFormModal, setShowFormModal, handleModalFormClose, handleModalFormSubmit, newsletterRef, successRef }} />
        </header>
    );
};

export {
    Header as default
};
export type { HeaderProps };
